/**
 * RelayHelperSection — live Relay Helper status plus the four setup steps
 * that mirror the Mac app, with the terminal route folded into an accordion.
 * Download fetches /api/helper/download with the auth header (a plain link
 * sends none) and navigates to the signed URL it returns; the version and date come from
 * /api/helper/latest (404 = nothing published yet, button disabled).
 */
import { useEffect, useState } from "react";
import { getAuthHeader } from "../../lib/auth";
import { CopyInstructionsButton } from "../CopyInstructionsButton";
import { buildHelperSetupInstructions } from "../../lib/copyInstructions";
import { relativeTime } from "./useHelperStatus";
import type { HelperStatus } from "./useHelperStatus";

interface HelperRelease { version: string; published_at: string | null }

function useHelperRelease(workspaceId: string) {
  const [release, setRelease] = useState<HelperRelease | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeader(workspaceId);
        const res = await fetch("/api/helper/latest", { headers });
        if (!res.ok) { if (!cancelled) setRelease(null); return; }
        const data = await res.json();
        if (!cancelled) setRelease({ version: data.version, published_at: data.published_at ?? null });
      } catch (err) {
        console.warn("[RelayHelperSection] latest release", err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);
  return { release, loaded };
}

function Accordion({ label, defaultOpen = false, children }: { label: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button className="tp-accordion-btn" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span className={`tp-chevron${open ? " open" : ""}`} aria-hidden="true">▸</span>
        {label}
      </button>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

function CommandLine({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(command); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard unavailable */ }
  }
  return (
    <div className="tp-secret" style={{ marginBottom: 0 }}>
      <div className="tp-secret-value" style={{ flexBasis: 120 }}>{command}</div>
      <button className="tp-btn-xs" onClick={copy}>{copied ? "✓ Copied" : "Copy"}</button>
    </div>
  );
}

function Steps({ hasKey, workspaceId }: { hasKey: boolean; workspaceId: string }) {
  const { release, loaded } = useHelperRelease(workspaceId);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  async function download() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const headers = await getAuthHeader(workspaceId);
      const res = await fetch("/api/helper/download", { headers: { ...headers, Accept: "application/json" } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) { setDownloadError(data.error || `Download failed (HTTP ${res.status})`); return; }
      window.location.assign(data.url);
    } catch (err) {
      console.warn("[RelayHelperSection] download", err);
      setDownloadError("Network error. Please try again.");
    } finally {
      setDownloading(false);
    }
  }
  const published = release?.published_at ? new Date(release.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null;
  return (
    <ol className="tp-steps">
      <li className="tp-step">
        <div className="tp-step-body">
          <div className="sc-item-name">1 · Download Relay Helper for Mac</div>
          <div className="tp-small">Open it. It lives in the menu bar.</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0 4px" }}>
            {release ? (
              <button className="btn primary" onClick={download} disabled={downloading}>{downloading ? "Preparing…" : "Download"}</button>
            ) : (
              <button className="btn primary" disabled>Download</button>
            )}
            {release && <span className="tp-small">Version {release.version}{published ? ` · ${published}` : ""}</span>}
            {loaded && !release && <span className="tp-small">Not published yet</span>}
          </div>
          {downloadError && <div className="sc-callout error" style={{ margin: "4px 0" }}>{downloadError}</div>}
          <div className="tp-small">First time? If macOS blocks it, open System Settings › Privacy &amp; Security and click Open Anyway.</div>
        </div>
      </li>
      <li className="tp-step">
        <div className="tp-step-body">
          <div className="sc-item-name">2 · Paste your key</div>
          <div className="tp-small">{hasKey ? "Paste the key from My Key." : "You need a key first. Create one above."}</div>
        </div>
      </li>
      <li className="tp-step">
        <div className="tp-step-body">
          <div className="sc-item-name">3 · Confirm your projects</div>
          <div className="tp-small">Relay Helper lists your Relay projects and finds each folder on this Mac.</div>
        </div>
      </li>
      <li className="tp-step">
        <div className="tp-step-body">
          <div className="sc-item-name">4 · Click Start</div>
          <div className="tp-small">Work pauses if your Mac sleeps. Keep it awake during work sessions.</div>
        </div>
      </li>
    </ol>
  );
}

function TerminalGuide() {
  const rows: Array<[string, string]> = [
    ["Install once. Asks for your key.", "relay install"],
    ["Start of session. Keep the window open.", "relay start"],
    ["End of session.", "relay stop"],
    ["See activity and agent output.", "relay logs"],
  ];
  return (
    <Accordion label="Use the terminal instead">
      <div className="tp-small">For a Mac or Linux machine without Relay Helper. Same key, same work.</div>
      <div className="tp-cmds">
        {rows.map(([label, cmd]) => (
          <div key={cmd} style={{ display: "contents" }}>
            <div className="tp-small">{label}</div>
            <CommandLine command={cmd} />
          </div>
        ))}
      </div>
      <div className="tp-small">
        If something seems stuck, run <code>relay stop</code>, then <code>relay start</code>. On a Mac, run <code>caffeinate -i</code> in a separate terminal to keep it awake.
      </div>
      <ul className="tp-small" style={{ margin: "8px 0 0", paddingLeft: 16 }}>
        <li><strong>Auth error?</strong> Make sure you’re authenticated with your cloud provider (for example <code>gcloud auth application-default login</code> for GCP), then run <code>relay start</code>.</li>
        <li><strong>Nothing happening?</strong> Make sure the terminal window is still open and try posting @RELAY in the Chat to test the connection.</li>
        <li><strong>Work stopped mid-sprint?</strong> Your computer may have gone to sleep. Run <code>relay stop</code> then <code>relay start</code> to reconnect.</li>
      </ul>
    </Accordion>
  );
}

export function RelayHelperSection({ workspaceId = "default", helper, hasKey }: { workspaceId?: string; helper: HelperStatus; hasKey: boolean }) {
  const rel = relativeTime(helper.lastHeartbeat);
  return (
    <section id="team-section-helper" style={{ marginBottom: 32 }}>
      <div className="sc-section-label">Relay Helper</div>
      <div className="tp-lede">The Mac app that runs AI partners in your project folders when Relay sends work.</div>

      <div className={`tp-status${helper.online ? "" : " off"}`} data-testid="helper-status-row">
        <span className={`tp-dot${helper.online ? "" : " off"}`} />
        <div>
          <div className="sc-item-name">{helper.online ? "Relay Helper" : "Relay Helper is offline"}</div>
          <div className="tp-small">
            {helper.online
              ? `Running${rel ? ` · last check-in ${rel}` : ""}`
              : "Open Relay Helper and click Start. Work waits until a Relay Helper is running."}
          </div>
        </div>
        <span className={`tp-badge${helper.online ? "" : " off"}`}>{helper.online ? "Running" : "Offline"}</span>
      </div>

      {helper.online ? (
        <div className="tp-card">
          <Accordion label="Set up another Mac"><Steps hasKey={hasKey} workspaceId={workspaceId} /></Accordion>
        </div>
      ) : (
        <div className="tp-card">
          <div className="tp-card-title">
            Set up Relay Helper
            <span className="tp-right"><CopyInstructionsButton text={buildHelperSetupInstructions()} label="Copy Instructions for Your AI" /></span>
          </div>
          <Steps hasKey={hasKey} workspaceId={workspaceId} />
        </div>
      )}

      <div className="tp-card"><TerminalGuide /></div>
    </section>
  );
}
